import { Component, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { AuthenticationService } from '@c4/auth';
import { Broker, Platform } from '@c4/core';
import { UIModule } from '@c4/ui';

import { SysmanComponentBase, IOnBrokerConnectedInit } from '../sysman/sysman.component';
import { ConnectedDeviceService, NetworkService, VersionsService } from '../services';
import { ActiveMenuItemsPipe } from '../pipes/active-menu-items.pipe';

interface Tab {
  name: string,
  key: string,
  hidden: boolean
}

@Component({
  selector: 'sysman-network-config',
  templateUrl: './sysman-network-config.component.html',
  styleUrls: ['./sysman-network-config.component.less']
})
export class SysmanNetworkConfigComponent extends SysmanComponentBase implements OnInit, IOnBrokerConnectedInit {

  initialNetworkName: string;

  initialEthernetNetworkConfig: any;
  initialWifiNetworkConfig: any;
  
  ethernetNetworkConfig: any;
  wifiNetworkConfig: any;

  activeNetworkConfig: string;

  isIdentified: boolean;
  cancelledWifiTest: boolean;
  noNetworkMsg: string = "No Network Selected";

  showTestWifiModalSpinner: boolean;
  invalidEthernetFormErrors: Array<string>;
  invalidWifiFormErrors: Array<string>;
  changeIPWarningMsg: string;

  // modal messages
  alertMessage: string;
  invalidEncryptionKeyMessage = "";
  isNotSupportedDevice: boolean;

  testWifiModal : any = { header: "", waitMsg: "", successMsg: "" };

  wifiNetworkMessage: string;
  wifiNetworks: Array<any>;

  networkName: string = "";
  connectedDevice: any;

  showTextPassword: boolean = false;

  allEncryptionTypes: Array<any> = [
    { name: "None", value: "" },
    { name: "WEP", value: "WEP" },
    { name: "WEP-128", value: "WEP128" },
    { name: "WPA2/WPA2 PSK", value: "WPA2" }];

  allEncryptionKeyTypes: Array<any> = [
    { name: "ASCII", value: "ascii" },
    { name: "Hex", value: "hex" }];

  DHCPRadioOptions: Array<any> = [
    { name: 'Obtain Automatically with DHCP', value: 'dhcp' },
    { name: 'Use the following', value: 'static' }];

  private tabs: Tab[] = [
    { name: "Device Name", key: "name", hidden: false },
    { name: "Ethernet Settings", key: "ethernet", hidden: false },
    { name: "Wireless Settings", key: "wireless", hidden: false }
  ]

  private activeTab: Tab = this.tabs[0];

  private showPleaseWaitModal: boolean = false;
  private showAlertModal: boolean = false;
  private showConfirmRebootModal: boolean = false;
  private showBrokerHostWaitModal: boolean = false;
  private showTestingWifiModal: boolean = false;
  private showConfirmNetworkNameModal: boolean = false;
  private showConfirmWifiRebootModal: boolean = false;

  constructor(private store: Store<any>, private router: Router, private route: ActivatedRoute,
    private broker: Broker, private platform: Platform,
    private connectedDeviceService: ConnectedDeviceService,
    private versionsService: VersionsService,
    private networkService: NetworkService,
    private auth: AuthenticationService) {
    super(router, route, store, broker, platform, connectedDeviceService, versionsService);
    super.setBrokerInit(this.onBrokerConnectedInit); // method to call on broker init
    this.connectedDevice = null;
    this.isNotSupportedDevice = false;
    this.initialNetworkName = "";
    this.changeIPWarningMsg = "";
    this.activeNetworkConfig = "";

    this.initialEthernetNetworkConfig = {
      name: "eth0",
      type: "", // "dhcp", "static"
      address: "",
      netmask: "",
      gateway: "",
      nstype: "", // DNS - "dhcp", "static"
      ns1: "",
      ns2: "",
      ns3: ""
    };

    this.initialWifiNetworkConfig = {
      name: "wlan0",
      type: "", // "dhcp", "static"
      nstype: "", // DNS - "dhcp", "static"
      essid: "", // ssid
      enc: "", // encryption
      key: "", // encryption key
      keytype: "ascii",
      address: "",
      netmask: "",
      gateway: "",
      ns1: "",
      ns2: "",
      ns3: ""
    };

    this.ethernetNetworkConfig = this.initialEthernetNetworkConfig;
    this.wifiNetworkConfig = this.initialWifiNetworkConfig;
    
    this.isIdentified = true;
    this.invalidEncryptionKeyMessage = "";
    this.wifiNetworkMessage = "";
    this.alertMessage = "";
    this.invalidEthernetFormErrors = [];
    this.invalidWifiFormErrors = [];

    this.showTestWifiModalSpinner = false;
    this.cancelledWifiTest = false;
    this.showTextPassword = false;
  }

  ngOnInit() {
    super.OnInit();
    this.wifiNetworks = [{ value: "", name: this.noNetworkMsg, enc: "", essid: "" }];
  }

  ngOnDestroy() {
    this.connectedDevice = null;
    console.debug('SysmanNetworkConfigComponent: onNgDestroy()');
    super.destroy();
  }

  onBrokerConnectedInit() {
    this.connDevice.take(1).subscribe(conn => {
      this.connectedDevice = conn.connectedDevice;
      let ip: string = null;

      if (this.brokerHost != this.connectedDevice.address)
        ip = this.connectedDevice.address;

      this.enableNetworkConfig();
      this.getNetworkName(ip);

      let isNetworkConfigSupported = true;
      if (this.connectedDevice.uuid) {
        // if this is a CA-1 or Trident remove WEP options
        if ((this.connectedDevice.uuid.indexOf("control4_tr1") > -1) || (this.connectedDevice.uuid.indexOf("control4_ca1") > -1))
          this.allEncryptionTypes = [{ name: "None", value: "" }, { name: "WPA2/WPA2 PSK", value: "WPA2" }];
        else // glassedge touch screens don't support network config
          if (this.connectedDevice.uuid.indexOf("glassedge") > -1) {
            this.disableNetworkConfig();
            isNetworkConfigSupported = false;
          }
      }
      if (isNetworkConfigSupported) {
        this.getAvailableWifiNetworks(ip);
        this.getEnabledNetwork(ip);
      }
    });
  }

  getAvailableWifiNetworks(ip?: string) {

    console.log("SysmanNetworkConfigComponent getAvailableWifiNetworks", ip);
    this.networkService.getWirelessNetworks(ip).then(
      (data: any) => {
        console.log("wifi network scan result: ", data);
        let wifis = [];

        if (data.length == 0)
          this.wifiNetworkMessage = "No Wireless Networks were found.";
        else {
          let isEssidFound: boolean = false;
          data.forEach(o => {
            if (o.essid == this.wifiNetworkConfig.essid)
              isEssidFound = true;
            o.name = o.essid;
            o.value = o.essid;
            if (o.enc == "None")
              o.enc = "";
            // avoid dupes
            var dupes = wifis.filter(item => {
              return (item.name == o.name);
            });
            if (dupes.length == 0)
              wifis.push(o);
          });
          // sort networks by name
          wifis = wifis.sort((a, b) => { if (a.name.toLowerCase() > b.name.toLowerCase()) return 1; if (b.name.toLowerCase() > a.name.toLowerCase()) return -1; return 0; });
          //this.wifiNetworkMessage = wifis.length + " Wireless Network(s) found.";
          this.wifiNetworkMessage = "";
        }
        this.wifiNetworks = this.wifiNetworks.concat(wifis);
      },
      (err: any) => {
        console.error('getWirelessNetworks() error: ', err);
        // error on finding wifi networks probably means device doesn't support wifi
        this.disableWifi();
      }
    );
  }

  saveInitialEthernetConfig(config: any) {
    this.initialEthernetNetworkConfig.name = config.name;
    this.initialEthernetNetworkConfig.type = config.type;
    if (config.address == undefined) {
      this.initialEthernetNetworkConfig.address = "";
      config.address = "";
    }
    if (config.netmask == undefined) {
      this.initialEthernetNetworkConfig.netmask = "";
      config.netmask = "";
    }
    if (config.gateway == undefined) {
      this.initialEthernetNetworkConfig.gateway = "";
      config.gateway = "";
    }
    if (config.nstype == undefined) {
      this.initialEthernetNetworkConfig.nstype = "dhcp";
      config.nstype = "dhcp";
    }
    if (config.ns1 == undefined) {
      this.initialEthernetNetworkConfig.ns1 = "";
      config.ns1 = "";
    }
    if (config.ns2 == undefined) {
      this.initialEthernetNetworkConfig.ns2 = "";
      config.ns2 = "";
    }
    if (config.ns3 == undefined) {
      this.initialEthernetNetworkConfig.ns3 = "";
      config.ns3 = "";
    }
  }

  saveInitialWifiConfig(config: any) {
    this.initialWifiNetworkConfig.name = config.name;
    this.initialWifiNetworkConfig.essid = (config.essid ? config.essid : "");
    
    if (config.enc == undefined) {
      this.initialWifiNetworkConfig.enc = "";
      config.enc = "";
    }    
    if (config.key == undefined) {
      this.initialWifiNetworkConfig.key = "";
      config.key = "";
    }    
    if (config.keytype == undefined) {
      this.initialWifiNetworkConfig.keytype = "ascii";
      config.keytype = "ascii";
    }
    if (config.type == undefined) {
      this.initialWifiNetworkConfig.type = "dhcp";
      config.type = "dhcp";
    }    
    if (config.address == undefined) {
      this.initialWifiNetworkConfig.address = "";
      config.address = "";
    }
    if (config.netmask == undefined) {
      this.initialWifiNetworkConfig.netmask = "";
      config.netmask = "";
    }
    if (config.gateway == undefined) {
      this.initialWifiNetworkConfig.gateway = "";
      config.gateway = "";
    }
    if (config.nstype == undefined) {
      this.initialWifiNetworkConfig.nstype = "dhcp";
      config.nstype = "dhcp";
    }
    if (config.ns1 == undefined) {
      this.initialWifiNetworkConfig.ns1 = "";
      config.ns1 = "";
    }
    if (config.ns2 == undefined) {
      this.initialWifiNetworkConfig.ns2 = "";
      config.ns2 = "";
    }
    if (config.ns3 == undefined) {
      this.initialWifiNetworkConfig.ns3 = "";
      config.ns3 = "";
    }    
  }

  getEnabledNetwork(ip?: string) {
    this.connectedDeviceService.setWorking(true);

    this.networkService.getNetworkInfo(ip).then(
      (data: any) => {
        console.log("getNetworkInfo:", data);
        this.connectedDeviceService.setWorking(false);
        if (Array.isArray(data)) {
          var networks = data.filter(network => {
            return (network.enabled == 1)
          });
          // set current enabled network settings
          if (networks.length > 0)
            this.activeNetworkConfig = networks[0].name; // wlan0 or eth0
          
          // prevent showing undefined in the key field
          if (data[1].key == undefined)
            data[1].key = "";

          this.ethernetNetworkConfig = data[0];
          this.saveInitialEthernetConfig(this.ethernetNetworkConfig);
          this.wifiNetworkConfig = data[1];
          this.saveInitialWifiConfig(this.wifiNetworkConfig);
          if (this.wifiNetworkConfig.essid != "")
            this.setEncryptionType(this.initialWifiNetworkConfig.essid);
        }
      },
      (err: any) => {
        this.connectedDeviceService.setWorking(false);
        console.error('getNetworkInfo() error: ', err);
      }
    );
  }

  isWirelessSettingsDirty(): boolean {
    return !((this.wifiNetworkConfig.essid == this.initialWifiNetworkConfig.essid) &&
      (this.wifiNetworkConfig.type == this.initialWifiNetworkConfig.type) &&
      (this.wifiNetworkConfig.enc == this.initialWifiNetworkConfig.enc) &&
      (this.wifiNetworkConfig.key == this.initialWifiNetworkConfig.key) &&
      (this.wifiNetworkConfig.keytype == this.initialWifiNetworkConfig.keytype) &&
      (this.wifiNetworkConfig.address == this.initialWifiNetworkConfig.address) &&
      (this.wifiNetworkConfig.netmask == this.initialWifiNetworkConfig.netmask) &&
      (this.wifiNetworkConfig.gateway == this.initialWifiNetworkConfig.gateway) &&
      (this.wifiNetworkConfig.nstype == this.initialWifiNetworkConfig.nstype) &&
      (this.wifiNetworkConfig.ns1 == this.initialWifiNetworkConfig.ns1) &&
      (this.wifiNetworkConfig.ns2 == this.initialWifiNetworkConfig.ns2) &&
      (this.wifiNetworkConfig.ns3 == this.initialWifiNetworkConfig.ns3));
  }

  isEthernetSettingsDirty(): boolean {
    // enable save button if initial config is wifi
    if (this.activeNetworkConfig == "wlan0")
      return true;
    else
      return !((this.ethernetNetworkConfig.type == this.initialEthernetNetworkConfig.type) &&
        (this.ethernetNetworkConfig.address == this.initialEthernetNetworkConfig.address) &&
        (this.ethernetNetworkConfig.netmask == this.initialEthernetNetworkConfig.netmask) &&
        (this.ethernetNetworkConfig.gateway == this.initialEthernetNetworkConfig.gateway) &&
        (this.ethernetNetworkConfig.nstype == this.initialEthernetNetworkConfig.nstype) &&
        (this.ethernetNetworkConfig.ns1 == this.initialEthernetNetworkConfig.ns1) &&
        (this.ethernetNetworkConfig.ns2 == this.initialEthernetNetworkConfig.ns2) &&
        (this.ethernetNetworkConfig.ns3 == this.initialEthernetNetworkConfig.ns3));
  }

  onClickCancelEdit() {
    this.router.navigate(['../../sysman/details'], { relativeTo: this.route });
  }

  onClickAlertOK() {
    this.showAlertModal = false;
    this.alertMessage = "";
  }

  // go back to disco page
  onClickPleaseWaitCancel() {
    this.showPleaseWaitModal = false;

    this.platform.getPlatformStatus().then(
      (result: any) => {
        this.connectedDeviceService.setWorking(false);
        let directorVersion = this.getDirectorVersion(result);
        this.connectedDeviceService.setConnection({ uuid: result.directorUUID, address: this.brokerHost, directorName: result.directorName, version: directorVersion });
        this.router.navigate(["../../sysman/disco"], { relativeTo: this.route });
      },
      (err: any) => {
        this.connectedDeviceService.setWorking(false);
        console.log('in onClickPleaseWaitCancel.getPlatformStatus() error', err);
      });
  }

  isValidConfig(config : any) {
    let invalidFormErrors = [];
    this.changeIPWarningMsg = "";

    // validate static IP
    if (config.type == "static") {
      if (config.address == "")
        invalidFormErrors.push("You must enter an IP Address when selecting the Static IP option.");
      else
        if (!this.connectedDeviceService.isValidIPAddress(config.address))
          invalidFormErrors.push("Please enter a valid IP Address.");

      if (config.netmask == "")
        invalidFormErrors.push("You must enter a Netmask when selecting the Static IP option.");
      else
        if (!this.connectedDeviceService.isValidIPAddress(config.netmask))
          invalidFormErrors.push("Please enter a valid address for Subnet.");

      if (config.gateway == "")
        invalidFormErrors.push("You must enter a Gateway when selecting the Static IP option.");
      else
        if (!this.connectedDeviceService.isValidIPAddress(config.gateway))
          invalidFormErrors.push("Please enter a valid address for Gateway.");
    }

    // validate DNS
    if (config.nstype == "static") {
      if ((config.ns1 == "") && (config.ns2 == "") && (config.ns3 == ""))
        invalidFormErrors.push("You must enter at least one DNS.");
      else {
        if ((config.ns1 != "") && (!this.connectedDeviceService.isValidIPAddress(config.ns1)))
          invalidFormErrors.push("Please enter a valid address for DNS 1.");

        if ((config.ns2 != "") && (!this.connectedDeviceService.isValidIPAddress(config.ns2)))
          invalidFormErrors.push("Please enter a valid address for DNS 2.");

        if ((config.ns3 != "") && (!this.connectedDeviceService.isValidIPAddress(config.ns3)))
          invalidFormErrors.push("Please enter a valid address for DNS 3.");
      }
    }

    if (invalidFormErrors.length == 0) { // check for displaying warning messages
      // if connected to primary broker - check if changing IP address
      if (this.connectedDevice.address == this.brokerHost) {
        // changing from static IP to DHCP
        if ((config.type == "static") && (config.type == "dhcp")) {
          this.changeIPWarningMsg = "Changing the IP Address from Static IP to DHCP will cause the connection to be lost!";
        }
        else // changing from DHCP to static IP & if new ip is not current ip
          if ((config.type == "dhcp") && (config.type == "static") &&
            (this.connectedDevice.address != config.address)) {
            this.changeIPWarningMsg = "Changing the IP Address will cause the connection to be lost!";
          }
          else // changing static IP address 
            if ((config.type == "static") && (config.type == "static") &&
              (config.address != config.address)) {
              this.changeIPWarningMsg = "Changing the IP Address will cause the connection to be lost!";
            }
      }
    }

    if (config.name == "eth0")
      this.invalidEthernetFormErrors = invalidFormErrors;
    else
      this.invalidWifiFormErrors = invalidFormErrors;

    // form is valid
    return (invalidFormErrors.length == 0);
  }

  onClickSaveEthernet() {
    if (this.isValidConfig(this.ethernetNetworkConfig))
      this.showConfirmRebootModal = true;
  }

  // save ethernet network settings
  onClickSaveEthernetOK() {
    this.cancelledWifiTest = false;
    this.showConfirmRebootModal = false;

    let sendPost = {};
    sendPost["settings"] = this.ethernetNetworkConfig;
    sendPost["save"] = true;

    this.networkService.saveNetworkInfo(sendPost, ((this.brokerHost != this.connectedDevice.address) ? this.connectedDevice.address : null)).then(
      (data: any) => {
        console.log('onClickSaveEthernet()', data);
        // if rebooting broker host then logout
        if (this.brokerHost == this.connectedDevice.address) {
          this.showBrokerHostWaitModal = true;
          this.logout();
        }
        else // if we are connecting to a slave then redirect back to discovery page
          this.showPleaseWaitModal = true;
      },
      (err: any) => {
        console.error('onClickSaveEthernet() error: ', err);
        this.showPleaseWaitModal = false;
        this.showBrokerHostWaitModal = false;
        this.alertMessage = "Error while saving network settings. " + (err.statusText ? err.statusText : "Unknown error");
        this.showAlertModal = true;
      });
  }

  onClickTestWifiCancel() {
    this.connectedDeviceService.setWorking(false);
    this.showTestingWifiModal = false;
    this.cancelledWifiTest = true;
  }

  onClickTestWifiOK() {
    this.showTestingWifiModal = false;
    this.onClickSaveWireless(false);
  }

  onClickSaveWirelessConfirm() {
    if (this.isValidConfig(this.wifiNetworkConfig))
      this.showConfirmWifiRebootModal = true;
  }

  onClickSaveWireless(isTest: boolean) {
    if (!this.isValidConfig(this.wifiNetworkConfig)) {
      this.showConfirmWifiRebootModal = false;
      this.showTestingWifiModal = false;
      return;
    }

    this.cancelledWifiTest = false;
    let sendPost = {};
    sendPost["settings"] = this.wifiNetworkConfig;

    if (isTest) {
      console.log("Testing Network Config", this.wifiNetworkConfig);
      sendPost["test"] = true; // test wireless
      this.testWifiModal.header = "Please Wait...";
      this.testWifiModal.waitMsg = "Testing Wireless Network Settings...";
      this.showTestWifiModalSpinner = true;
      this.showTestingWifiModal = true;
    }
    else {
      console.log("Saving Network Config", this.wifiNetworkConfig);
      sendPost["save"] = true;
      this.showConfirmWifiRebootModal = false;
    }

    this.connectedDeviceService.setWorking(true);
    this.networkService.saveNetworkInfo(sendPost, ((this.brokerHost != this.connectedDevice.address) ? this.connectedDevice.address : null)).then(
      (data: any) => {
        console.log('onClickSaveWireless()', data, (data == null), data.length);
        this.connectedDeviceService.setWorking(false);
        // for the case of the controller rebooting - test for empty object returned from broker
        if ((Object.keys(data).length === 0) && (data.constructor === Object)) {
            // if rebooting broker host then logout
            if (this.brokerHost == this.connectedDevice.address) {
              this.showBrokerHostWaitModal = true;
              this.logout();
            }
            else // if we are connecting to a slave then redirect back to discovery page
              this.showPleaseWaitModal = true;
        }
        else
        if (data.status == "success") {
          if (isTest) {
            if (this.cancelledWifiTest) { // ignore anything that comes back if test is canceled
              this.showTestWifiModalSpinner = false;
              this.showTestingWifiModal = false;
            } else {
              this.showTestWifiModalSpinner = false;
              this.testWifiModal.header = "Test is Successful";
              this.testWifiModal.successMsg = "Press OK if you want to save these settings. Your controller will reboot";
            }
          }
          else {
            // if rebooting broker host then logout
            if (this.brokerHost == this.connectedDevice.address) {
              this.showBrokerHostWaitModal = true;
              this.logout();
            }
            else // if we are connecting to a slave then redirect back to discovery page
              this.showPleaseWaitModal = true;
          }
        }
        else { // failure occurred
          this.showTestWifiModalSpinner = false;
          this.showTestingWifiModal = false;
          this.showPleaseWaitModal = false;
          this.showBrokerHostWaitModal = false;
          if (isTest) {
              this.alertMessage = "Test Failed. Please check your settings and try again. " + (data.status ? data.status : "");
          }
          else {
              this.alertMessage = "Error while saving network settings. " + 
                  "An unknown error occurred, please check the network settings and try again. " + (data.status ? data.status : "");                
          }
          this.showAlertModal = true;
        }
      },
      (err: any) => {
        // Test failed?
        this.connectedDeviceService.setWorking(false);

        console.error('onClickSaveWireless() error: ', err);
        this.showTestWifiModalSpinner = false;
        this.showTestingWifiModal = false;
        this.showPleaseWaitModal = false;
        this.showBrokerHostWaitModal = false;
        if (!this.cancelledWifiTest) {
          this.alertMessage = "Error while " + (isTest ? "testing" : "saving") + " network settings. " + 
                              "An unknown error occurred, please check the network settings and try again." +
                              (err._body ? JSON.parse(err._body).error : (err.statusText ? err.statusText : "Unknown Error"));
          this.showAlertModal = true;
        }
      });
  }

  networkIPTypeChanged(iptype: string, config: any) {
    config.type = iptype;
    // force DNS type to be static when static IP is chosen
    if (iptype == "static")
      config.nstype = iptype;
    else { // dhcp
      config.address = "";
      config.netmask = "";
      config.gateway = "";
    }
  }

  onClickSaveNetworkName() {
    this.showConfirmNetworkNameModal = true;
  }

  onClickRefreshNetworkName() {
    this.getNetworkName(this.connectedDevice.address);
  }

  getNetworkName(ip?: string) {
    this.isNotSupportedDevice = false;
    this.networkService.getNetworkName(ip).then(
      (data: any) => {
        console.log('getNetworkName(): ', data);
        if (data.hostname) {
          this.networkName = data.hostname;
          this.initialNetworkName = data.hostname;
          this.isNotSupportedDevice = this.networkService.isNotSupportedDevice(data.hostname);
        }
      },
      (err: any) => {
        console.error('getNetworkInfo() error: ', err);
      }
    );

    this.isIdentified = true;
    this.platform.getPlatformStatus().then(
      (result: any) => {
        this.isIdentified = result.isIdentified;
      },
      (err: any) => {
        console.log('in getNetworkName.getPlatformStatus() error', err);
      });
  }

  saveNetworkName() {
    this.showConfirmNetworkNameModal = false;
    this.connectedDeviceService.setWorking(true);

    this.networkService.saveNetworkName(this.networkName, ((this.brokerHost != this.connectedDevice.address) ? this.connectedDevice.address : null)).then(
      (data: any) => {
        this.connectedDeviceService.setWorking(false);
        console.log('onClickSaveNetworkName()', data);

        // if rebooting broker host then logout
        if (this.brokerHost == this.connectedDevice.address) {
          this.showBrokerHostWaitModal = true;
          this.logout();
        }
        else // if we are connecting to a slave then redirect back to discovery page
          this.showPleaseWaitModal = true;

        // now reboot device
        this.networkService.rebootDevice(((this.brokerHost != this.connectedDevice.address) ? this.connectedDevice.address : null)).then(
          (data: any) => { // this may not come back
            console.log('onClickSaveNetworkName() rebooting: ', data);
          },
          (err: any) => { // most likely returns an error
          });
      },
      (err: any) => {
        this.connectedDeviceService.setWorking(false);
        console.error('onClickSaveNetworkName() error: ', err);
        this.showPleaseWaitModal = false;
        this.showBrokerHostWaitModal = false;
        this.alertMessage = "Error while saving network settings. " + (err.statusText ? err.statusText : "Unknown error");
        this.showAlertModal = true;
      });
  }

  private disableNetworkConfig() {
      this.tabs[1].hidden = true;   
      this.tabs[2].hidden = true;
  }

  private enableNetworkConfig() {
      this.tabs[1].hidden = false;   
      this.tabs[2].hidden = false;   
  }

  private enableWifi() {
      this.tabs[2].hidden = false;   
  }

  private disableWifi() {
      this.tabs[2].hidden = true;
  }

  private isEncryptionKeyValid() {

    this.invalidEncryptionKeyMessage = "";
    if (this.wifiNetworkConfig.enc == "")
      return true;

    if (this.wifiNetworkConfig.keytype == 'ascii') {
      if ((!this.wifiNetworkConfig.key || (this.wifiNetworkConfig.key == "")) ||
        ((this.wifiNetworkConfig.key.length < 8) || (this.wifiNetworkConfig.key.length > 63))) {
        this.invalidEncryptionKeyMessage = "The Encryption Key must be at between 8 and 63 characters long.";
        return false;
      }
    }
    else
      if (this.wifiNetworkConfig.keytype == 'hex') {
        let retval = true;
        // check for valid key - spaces are ok
        if ((!this.wifiNetworkConfig.key || (this.wifiNetworkConfig.key == "")) || (this.wifiNetworkConfig.key.replace(/ /g, "").length != 64)) {
          this.invalidEncryptionKeyMessage = "The Encryption Key must be 64 hexdigits long.";
          retval = false;
        } else
          if (!this.isHexChars(this.wifiNetworkConfig.key)) {
            this.invalidEncryptionKeyMessage += " The Encryption Key must be only hexdigits.";
            retval = false;
          }
        return retval;
      }
    return true;
  }

  onNSTypeChange(value : string, config : any) {
    config.nstype = value;
    if (value == "dhcp") {
      config.ns1 = "";
      config.ns2 = "";
      config.ns3 = "";
    }
  }

  onChangeWifiNetwork(event : any) {
    this.wifiNetworkConfig.essid = event.target.value;
    this.setEncryptionType(event.target.value);
  }

  private setEncryptionType(essid : string) {
    // clear out enc type if no network is selected
    if (essid == "") {
      this.wifiNetworkConfig.enc = "";
      return;    
    }
    var wifiNetworks = this.wifiNetworks.filter(network => {
      return (network.name == essid)
    });
    if (wifiNetworks.length > 0) {
      // set encryption type for chosen wifi network
      this.wifiNetworkConfig.enc = wifiNetworks[0].enc;
    }
  }

  private isHexChars(input: string) {
    return /[0-9A-F]$/i.test(input);
  }

  private logout() {
    // display modal for 5 seconds then go back to dashboard
    setTimeout(o => {
      this.router.navigate(['/']);
    }, 5000);
  }
}


// WEBPACK FOOTER //
// ./~/@c4/sysman/src/app/sysman-network-config/sysman-network-config.component.ts